import React, { ComponentType, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import useUnleashFeatureFlagAdapter from '../../adapter/UnleashFeatureFlagAdapter';

type WithFeatureFlagApi<P> = {
  IsEnabled: (
    FallbackComponent?: ComponentType<P>,
  ) => ComponentType<P>;
  RedirectTo: (path?: string) => ComponentType<P>;
};

type WithFeatureFlagOptions = {
  /** Consulta a flag por usuário (isUserEnabled) em vez da flag global. */
  perUser?: boolean;
};

function WithFeatureFlag<P extends Record<string, unknown> = Record<string, unknown>>(
  WrappedComponent: ComponentType<P>,
  flagName: string,
  options: WithFeatureFlagOptions = {},
): WithFeatureFlagApi<P> {
  const IsEnabled = (FallbackComponent?: ComponentType<P>) => {
    return function FeatureWrapper(props: P) {
      const adapter = useUnleashFeatureFlagAdapter();

      const { isEnabled, isUserEnabled } = adapter || {};
      const checkFlag = options.perUser ? isUserEnabled : isEnabled;
      const [enabled, setEnabled] = useState<boolean | null>(null);

      useEffect(() => {
        if (!checkFlag) {
          setEnabled(false);
          return;
        }

        let mounted = true;

        (async () => {
          try {
            const result = await checkFlag(flagName);
            if (mounted) setEnabled(result);
          } catch (err) {
            console.error('Erro ao verificar feature flag:', flagName, err);
            if (mounted) setEnabled(false);
          }
        })();

        return () => {
          mounted = false;
        };
      }, [checkFlag]);

      if (enabled === null) return null;

      if (!enabled && !FallbackComponent) return null;

      return enabled ? (
        <WrappedComponent {...props} />
      ) : (
        <FallbackComponent {...props} />
      );
    };
  };

  const RedirectTo = (path = '/dashboard') => {
    return function FeatureRedirect(props: P) {
      const adapter = useUnleashFeatureFlagAdapter();

      const { isEnabled, isUserEnabled, flagsReady } = adapter || {};
      const checkFlag = options.perUser ? isUserEnabled : isEnabled;
      const [enabled, setEnabled] = useState<boolean | null>(null);

      useEffect(() => {
        if (!checkFlag) {
          setEnabled(false);
          return;
        }

        let mounted = true;

        (async () => {
          try {
            const result = await checkFlag(flagName);
            if (mounted) setEnabled(result);
          } catch (err) {
            console.error('Erro ao verificar feature flag:', flagName, err);
            if (mounted) setEnabled(false);
          }
        })();

        return () => {
          mounted = false;
        };
      }, [checkFlag]);

      if (enabled === null) return null;

      return enabled ? (
        <WrappedComponent {...props} />
      ) : flagsReady ? (
        <Redirect to={path} />
      ) : null;
    };
  };

  return {
    IsEnabled,
    RedirectTo,
  };
}

export default WithFeatureFlag;
